// module imports
import { debounce, DebounceSettings } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

export function useDebouncedCallback<T extends (...args: any[]) => any>(callback: T, delay: number = 300, options?: DebounceSettings): T & { cancel: () => void; flush: () => void } {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedCallback = useMemo(
    () =>
      debounce(
        (...args: Parameters<T>) => {
          callbackRef.current(...args);
        },
        delay,
        options
      ),
    [delay, options]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedCallback.cancel();
    };
  }, [debouncedCallback]);

  return debouncedCallback as T & { cancel: () => void; flush: () => void };
}

export function useDebouncedSearch<T extends (...args: any[]) => any>(searchFunction: T, delay: number = 300) {
  const debouncedSearch = useDebouncedCallback(searchFunction, delay);

  const cancelSearch = useCallback(() => {
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  const flushSearch = useCallback(() => {
    debouncedSearch.flush();
  }, [debouncedSearch]);

  return {
    search: debouncedSearch,
    cancel: cancelSearch,
    flush: flushSearch,
  };
}
